// src/components/ColumnSelector/ColumnSelector.jsx
import React, { useRef } from "react";
import { Layout, X } from "lucide-react";
import useClickOutside from "../../hooks/useClickOutside";

const ColumnSelector = ({
  visibleColumns,
  setVisibleColumns,
  columnOptions, // [{ key: string, label: string }]
  position = "bottom-right", // optional: "bottom-left", "top-right", etc.
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = useRef(null);

  useClickOutside([containerRef], () => setIsOpen(false));

  const handleToggle = (key) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSelectAll = () => {
    const allSelected = columnOptions.reduce((acc, col) => {
      acc[col.key] = true;
      return acc;
    }, {});
    setVisibleColumns(allSelected);
  };

  const handleUnselectAll = () => {
    const allUnselected = columnOptions.reduce((acc, col) => {
      acc[col.key] = false;
      return acc;
    }, {});
    setVisibleColumns(allUnselected);
  };

  const allSelected = columnOptions.every((col) => visibleColumns[col.key]);
  const someSelected = columnOptions.some((col) => visibleColumns[col.key]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-transparent border border-[#5A4479] rounded-full text-[#CCCCCC] hover:bg-[#2A2A2A] transition-colors duration-200 text-sm font-light"
      >
        <Layout className="w-4 h-4" />
        <span>Columns</span>
        {!allSelected && someSelected && (
          <X
            className="w-5 h-5 text-red-400 cursor-pointer hover:text-red-300"
            onClick={(e) => {
              e.stopPropagation();
              handleSelectAll();
            }}
          />
        )}
      </button>

      {isOpen && (
        <div
          className={`absolute ${position === "bottom-left" ? "left-0" : "right-0"} mt-2 w-64 bg-[#0a1733] border border-[#5A4479] rounded-lg shadow-xl z-50 p-5`}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-[#D9D9D9]">Select Columns</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex gap-2 mb-4">
            <button
              onClick={handleSelectAll}
              className="flex-1 px-3 py-1.5 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full text-xs hover:opacity-90 transition"
            >
              Select All
            </button>
            <button
              onClick={handleUnselectAll}
              className="flex-1 px-3 py-1.5 border border-gray-600 rounded-full text-gray-300 hover:bg-gray-700/50 text-xs transition"
            >
              Unselect All
            </button>
          </div>

          <div className="space-y-3 max-h-64 overflow-y-auto">
            {columnOptions.map(({ key, label }) => (
              <label
                key={key}
                className="flex items-center cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  checked={visibleColumns[key] || false}
                  onChange={() => handleToggle(key)}
                  className="w-4 h-4 text-purple-500 rounded border-gray-600 focus:ring-purple-500 focus:ring-offset-0"
                />
                <span className="ml-3 text-sm text-[#D8D8D8]">{label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnSelector;